import { useEffect, useMemo } from 'react'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { Table } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useBreakpoint } from '~/hooks'
import Tooltip from '~/components/Tooltip'
import { formatNumber } from '~/utils/numbers'
import { getExplorerTransactionLink } from '~/utils/chain'
import { Mainnet, ArbitrumOne } from '~/model/chain/ethereum'
import useSWR from 'swr'
import { getBuybackHistory } from '~/apis'
import {
  StyledBuybackHistoryListContainer,
  StyledHeader,
  StyledTableDateItem,
  StyledTableBuyBackAmountItem,
  StyledTableStatusItem,
  StyledIcon,
  StyledIconLink,
  StyledHeaderIcon,
} from './styled'
import IconSuccess from '~/assets/images/status-success-round.svg'
import IconInprogress from '~/assets/images/status-inprogress-round.svg'
import IconLink from '~/assets/images/link-rectangle-purple.svg'

enum BuybackStatus {
  DONE = 'done',
  INPROGRESS = 'inprogress',
}

const mintFactor = 1

// OKX 下架 LON 导致链下回购停止，改成手动回购的记录。
interface MixedBuybackHistoryItem {
  buybackAmount: number
  buybackFee: number
  endDate: string
  finished: boolean
  startDate: string
  txHash: string
  mintAmount?: number // frontend added
  isArbitrum?: boolean // frontend added
}
const fixedHistory: MixedBuybackHistoryItem[] = [
  {
    buybackAmount: 58941.81,
    buybackFee: 42000,
    endDate: '2025-07-10T08:00:00Z',
    finished: true,
    startDate: '2025-05-08T08:00:00Z',
    txHash:
      '0x265ecc779fecde5a0f082345222755bb1e0b2fee1f0ce701c437727cf13bdbf2',
    mintAmount: 0,
    isArbitrum: true,
  },
  {
    buybackAmount: 103835.088,
    buybackFee: 78000,
    endDate: '2025-05-08T08:00:00Z',
    finished: true,
    startDate: '2025-01-10T08:00:00Z',
    txHash:
      '0x0b3334a638920f8a4c1bf3ccfdf44bd86a12a1a8bdf2c03eef22c3a56ec4bbd0',
    mintAmount: 0,
    isArbitrum: true,
  },
  {
    buybackAmount: 41790,
    buybackFee: 35000,
    endDate: '2025-01-10T08:00:00Z',
    finished: true,
    startDate: '2025-01-06T08:00:00Z',
    txHash:
      '0xe1430ce72f5255f24fb74ecdf3794d9c8260f785aaaa6bed7f4926b89378532a',
    mintAmount: 0,
    isArbitrum: true,
  },
  {
    buybackAmount: 403954.9337472324,
    buybackFee: 325862.0265,
    endDate: '2025-01-06T08:00:00Z',
    finished: true,
    startDate: '2024-12-16T08:00:00Z',
    txHash:
      '0x7b5ba8835d639827fed9f32218f9bfc34ccc9360d957dd6d89a124178da73981',
  },
  {
    buybackAmount: 405415.36728587,
    buybackFee: 335439.01,
    endDate: '2024-12-16T08:00:00Z',
    finished: true,
    startDate: '2024-11-25T08:00:00Z',
    txHash:
      '0x0c622c29f97ba9a8dd39718a50445cf89951fc419f1e10adb3cc68377d2e54a2',
  },
]

const BuybackHistoryList: React.FC = () => {
  const { t } = useTranslation()
  const { isMobile } = useBreakpoint()

  const { data: buybackHistory, isValidating } = useSWR(
    'getBuybackHistory',
    getBuybackHistory,
  )

  const dataSource = useMemo(() => {
    if (!buybackHistory) {
      return []
    }

    // @notice: 后端返回的数据是从旧到新，并且从 2024.11.25 链下回购停止后多返回了6条不需要的数据。另外后端接口已经停止更新时间周期，所以前端没有使用 filter 而是直接 slice 到 66 的元素即可
    const slicedHistory = buybackHistory.slice(0, 66).reverse()
    const constructedHistory: MixedBuybackHistoryItem[] = [
      ...fixedHistory,
      ...slicedHistory,
    ]

    return constructedHistory.map((item, index) => {
      const {
        startDate,
        endDate,
        txHash,
        buybackAmount,
        buybackFee,
        mintAmount: realMintAmount,
        isArbitrum,
      } = item
      const date = `${dayjs(startDate).format('YYYY-MM-DD')} ~ ${dayjs(
        endDate,
      ).format('YYYY-MM-DD')}`
      const status = txHash ? BuybackStatus.DONE : BuybackStatus.INPROGRESS
      const mintAmount = realMintAmount ?? buybackAmount * mintFactor
      const buybackFeeLiteral = buybackFee
        ? `$${formatNumber(buybackFee, 2)}`
        : '-'
      const buybackAmountLiteral = buybackAmount
        ? `${formatNumber(buybackAmount, 2)} LON`
        : '-'
      const mintAmountLiteral = mintAmount
        ? `${formatNumber(mintAmount, 2)} LON`
        : '-'
      const chainId = isArbitrum ? ArbitrumOne.chainId : Mainnet.chainId
      const txHashLink = txHash
        ? getExplorerTransactionLink(txHash, chainId)
        : ''

      return {
        ...item,
        key: index,
        date,
        buybackFee: buybackFeeLiteral,
        buybackAmount: buybackAmountLiteral,
        mintAmount: mintAmountLiteral,
        status,
        txHashLink,
      }
    })
  }, [buybackHistory])

  const columns: ColumnsType<any> = [
    {
      title: t('buyback_history_list_title_date'),
      dataIndex: 'date',
      render: (date: string) => {
        return <StyledTableDateItem>{date}</StyledTableDateItem>
      },
      width: isMobile ? 196 : 312,
    },
    {
      title: (
        <div>
          <span>{t('buyback_history_list_title_buyback_fee')}</span>
          <Tooltip
            title={t('buyback_history_list_title_buyback_fee_tip')}
            trigger="hover"
            placement="topLeft"
          />
        </div>
      ),
      dataIndex: 'buybackFee',
      width: '17%',
    },
    {
      title: t('buyback_history_list_title_buyback_amount'),
      dataIndex: 'buybackAmount',
      render: (buybackAmount: string) => {
        return (
          <StyledTableBuyBackAmountItem>
            {buybackAmount}
          </StyledTableBuyBackAmountItem>
        )
      },
      width: '17%',
    },
    {
      title: t('buyback_history_list_title_mint_amount'),
      dataIndex: 'mintAmount',
      width: '17%',
    },
    {
      title: t('buyback_history_list_title_status'),
      dataIndex: 'status',
      render: (status: BuybackStatus, item) => {
        const isDone = status === BuybackStatus.DONE
        const iconSrc = isDone ? IconSuccess : IconInprogress
        return (
          <StyledTableStatusItem>
            <StyledIcon src={iconSrc} />
            {isDone && (
              <a href={item.txHashLink} target="_blank">
                <StyledIconLink src={IconLink} />
              </a>
            )}
          </StyledTableStatusItem>
        )
      },
      fixed: 'right',
      width: isMobile ? 118 : 238,
    },
  ]

  useEffect(() => {
    if (isMobile) {
      // scroll to fees of buyback column
      const scrollContainer = document.querySelector('.ant-table-content')
      scrollContainer.scrollLeft = 196
    }
  })

  return (
    <StyledBuybackHistoryListContainer>
      <StyledHeader>
        <span className="left-part">{t('buyback_history_list_title')}</span>
        <div className="right-part">
          <div className="status-item">
            <StyledHeaderIcon src={IconInprogress} />
            <span>{t('in_progress')}</span>
          </div>
          <div className="status-item">
            <StyledHeaderIcon src={IconSuccess} />
            <span>{t('done')}</span>
          </div>
        </div>
      </StyledHeader>
      <Table
        columns={columns}
        dataSource={dataSource}
        showSorterTooltip={false}
        pagination={{
          pageSize: 6,
          showSizeChanger: false,
          showQuickJumper: false,
          size: isMobile ? 'small' : 'default',
        }}
        loading={isValidating}
        scroll={!!dataSource?.length ? { x: true } : null}
        rowKey="date"
      />
    </StyledBuybackHistoryListContainer>
  )
}

export default BuybackHistoryList
